import React, { useRef, useEffect, useState } from "react";
import { Doughnut, Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import ErrorBoundary from "../../../../components/Common/ErrorBoundary";

ChartJS.register(ArcElement, Tooltip, Legend);

const colorMap = {
    Open: ['rgb(220, 138, 146)', 'rgb(240, 53, 71)'],
    Acknowledged:['rgb(120, 142, 224)', 'rgb(58, 99, 249)'],
    Closed: ['rgb(146, 225, 165)', 'rgb(10, 235, 63)']
  };

const fallbackColors = [
  ['#d06841', '#fa6028'],
  ['#dd2121', '#fa4646'],
  ['#daa926', '#f7b607'],
  ['#83ba2b', '#96eb0e'],
  ['#2b8eba', '#0ee3eb'],
  ['#7e41d0', '#b728fa'],
];

const PieChart = ({
  isDarkTheme,
  PieWidgetDetails,
  widgetConfig,
  handleModalShow,
  widgetData,
}) => {
  const canvasRef = useRef(null);
  const [chartData, setChartData] = useState(null);
  const { title, colorCodes } = PieWidgetDetails;

  const {
    WidgetName = "Pie Chart",
    WidgetID = "",
    Datapoints = [],
    Entity = "",
  } = widgetConfig || {};

  const { WidgetData = {} } = widgetData || {};

  const yName = Datapoints && Datapoints.length > 0 ? Datapoints[0] : "y-axis";
  const xName = Datapoints && Datapoints.length > 1 ? Datapoints[1] : "x-axis";

  const labels = WidgetData[xName] || [];
  const data = WidgetData[yName] || [];

  useEffect(() => {
    if (!canvasRef.current) return;
    if (!labels.length || !data.length){
      setChartData(null);
      return;
    } 

    const ctx = canvasRef.current.getContext("2d");

    // Use colorMap if label exists, else fallback to fallbackColors
    const gradients = labels.map((label, i) => {
      const [start, end] = colorMap[label] || fallbackColors[i % fallbackColors.length];
      const grad = ctx.createLinearGradient(0, 0, 0, 200);
      grad.addColorStop(0, start);
      grad.addColorStop(1, end);
      return grad;
    });

    setChartData({
      labels: labels,
      datasets: [
        {
          data: data,
          backgroundColor: gradients,
          hoverOffset: 4,
          borderWidth: 2,
        },
      ],
    });
  }, [widgetConfig, widgetData]);

  const options = {
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: {
          boxWidth: 14,
          color:  isDarkTheme ? '#555' : '#bababa'
        },
      },
    },
    onClick: (event, elements, chart) => {
      if (elements && elements.length > 0 && chartData) {
        const element = elements[0];
        const index = element.index;
        const filter = {[xName]: chartData.labels[index]}

        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, '0');
        const dd = String(today.getDate()).padStart(2, '0');
        const csvFileName = `${Entity.toLowerCase()}_${chartData.labels[index].toLowerCase()}_data_${yyyy}-${mm}-${dd}`;

        handleModalShow && handleModalShow(filter, Entity, WidgetID, csvFileName);
      }
    },
    onHover: (event, elements) => {
      if (event?.native) {
        event.native.target.style.cursor =
          elements && elements.length > 0 ? "pointer" : "default";
      }
    },
  };

  return (
  <ErrorBoundary>
    <div
      className="systemHealth m-0 WidgetPadding p-0"
      key={`widget-${WidgetID}`}
    >
      <p className="chartHeaderP p-2">{WidgetName}
        { widgetConfig.DateRange
          ? widgetConfig.DateRange === 1
            ? ` `
            : ` - ${widgetConfig.DateRange} Days`
          : ''}
      </p>
      <div
        style={{ height: "85%", display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <canvas ref={canvasRef} style={{ display: "none" }}></canvas>
        {!chartData ? (
          <span style={{ color: "#888", fontWeight: 500 }}>No Data Available</span>
        ) : (
          <Pie data={chartData} options={options} />
        )}
      </div>
    </div>
  </ErrorBoundary>
);
};

export default PieChart;